import { makeArticle } from "./learnFactory";
import type { LearnFAQ } from "./learnShared";
import type { RelatedLink } from "@/components/RelatedPages";

const faqs: LearnFAQ[] = [
  {
    question: "What indications qualify a patient for SAINT® TMS referral?",
    answer:
      "SAINT® (Stanford Accelerated Intelligent Neuromodulation Therapy) on the Magnus System is FDA-cleared (K220177) for the treatment of Major Depressive Disorder (MDD) in adult patients (aged 22 and older) who have failed to achieve satisfactory improvement from prior antidepressant medication in the current episode. Patients typically have tried two or more antidepressants of adequate dose and duration (treatment-resistant depression).",
  },
  {
    question: "How does co-management and patient handback work after SAINT®?",
    answer:
      "Neuro Wellness operates exclusively as an interventional neuromodulation facility; we do not retain patients for routine outpatient psychiatric care. Within five business days of protocol completion, we transmit a comprehensive clinical handback summary to your office, including baseline and post-treatment psychometrics (PHQ-9/MADRS), delivered treatment coordinates, motor threshold records, and follow-up recommendations. Patients return directly to your care for ongoing medication management and psychotherapy.",
  },
  {
    question: "What are the absolute contraindications to SAINT® TMS?",
    answer:
      "Absolute contraindications include non-removable ferromagnetic metal in or within 30 cm of the head (such as aneurysm clips, cochlear implants, deep brain stimulators, metal fragments), implanted medical devices controlled by physiological signals (cardiac pacemakers, ICDs, VNS stimulators), active uncontrolled seizure disorders, or known increased intracranial pressure. Patients must also be eligible to undergo a 3T structural and resting-state functional MRI scan for targeting.",
  },
  {
    question: "How do referring physicians send patient records securely?",
    answer:
      "To safeguard protected health information (PHI), referring offices should fax clinical notes, medication histories, and our completed [[Physician Referral & Eligibility Checklist|/patient-eligibility-checklist]] to our confidential clinical fax at 954-827-9793. For peer-to-peer case consultation or urgent review, call our direct clinician line at 954-827-9793.",
  },
  {
    question:
      "Does the patient need to taper off antidepressants before SAINT®?",
    answer:
      "No. Patients do not need to discontinue their stable antidepressant or psychotropic regimen before SAINT® unless a medication significantly lowers seizure threshold. Medication stability during the treatment week is typically preferred so that acute symptom changes can be accurately monitored. Any dosage modifications are coordinated directly with the referring prescriber.",
  },
];

const relatedLinks: RelatedLink[] = [
  {
    title: "Download One-Page Referral Checklist",
    href: "/patient-eligibility-checklist",
    description: "Printable PDF and clinical checklist for referring practices",
  },
  {
    title: "SAINT® Protocol Explained",
    href: "/learn/saint-tms-protocol",
    description: "50 sessions over 5 days: dosing, intervals, and mechanism",
  },
  {
    title: "fMRI-Guided TMS Targeting",
    href: "/learn/fmri-guided-tms-for-depression",
    description:
      "How resting-state fMRI identifies individual sgACC-dlPFC connectivity",
  },
  {
    title: "SAINT® TMS Success Rates",
    href: "/learn/saint-tms-success-rates",
    description:
      "Randomized controlled trial outcomes and durability timepoints",
  },
  {
    title: "What Is a Verified SAINT® Provider?",
    href: "/learn/what-is-a-verified-saint-provider",
    description:
      "Five clinical checks confirming authorized Magnus Medical technology",
  },
];

export const pageReferPatient = makeArticle({
  slug: "refer-a-patient-for-saint",
  title: "Refer a Patient for SAINT® TMS: Physician Guide & Co-Management",
  description:
    "Physician guide for referring patients for SAINT® accelerated TMS: clinical indications, contraindications, 5-day workflow, and patient handback protocol.",
  canonical: "/learn/refer-a-patient-for-saint",
  h1: "Referring a Patient for SAINT® TMS: Clinician Guide & Co-Management",
  breadcrumbLabel: "Refer a Patient for SAINT®",
  shortAnswer:
    "Neuro Wellness TMS Centers of America partners with psychiatrists, primary care physicians, neurologists, and therapists to deliver SAINT® accelerated TMS (50 sessions over 5 days, fMRI-guided) for adults with treatment-resistant depression. We practice strict clinical co-management: our team delivers the specialized 5-day protocol and resting-state fMRI targeting, and immediately returns the patient to your practice with a detailed post-treatment psychometric and parameter report. Secure referrals are accepted via HIPAA fax at 954-827-9793.",
  keyTakeaways: [
    "FDA-cleared indication: Adults 22+ with Major Depressive Disorder who have not improved satisfactorily on antidepressant medication.",
    "5-day protocol: 10 daily sessions of intermittent theta-burst stimulation (iTBS) with 50-minute inter-session intervals on the Magnus System.",
    "Individualized targeting: Resting-state fMRI identifies anti-correlated subgenual anterior cingulate cortex (sgACC) to dorsolateral prefrontal cortex (dlPFC) coordinates.",
    "Zero patient poaching: We handle neuromodulation only; long-term medication management and therapy remain with the referring clinician.",
    "Secure referral channel: Fax records and the [[Patient Eligibility Checklist|/patient-eligibility-checklist]] to 954-827-9793.",
  ],
  sections: [
    {
      heading: "Overview: The Clinician Partnership Model",
      paragraphs: [
        "Treatment-resistant depression (TRD) presents an immense clinical challenge: after two failed antidepressant trials, the likelihood of achieving remission with a subsequent oral agent drops to less than 15% (STAR*D trial, Rush et al., 2006). For patients suffering severe, refractory symptoms who cannot spend 6 to 9 weeks in conventional daily TMS or who cannot tolerate pharmacologic side effects, SAINT® (Stanford Accelerated Intelligent Neuromodulation Therapy) offers an evidence-based, rapid alternative.",
        "Neuro Wellness TMS Centers of America in Coral Springs, FL, operates under a cooperative specialist model. We view ourselves as an extension of your diagnostic and interventional toolkit. We manage the pre-treatment functional MRI targeting scan, administer the intensive 50-session week on the FDA-cleared Magnus Neuromodulation System, monitor acute tolerability, and promptly transfer care back to your practice for maintenance.",
      ],
    },
    {
      heading: "Clinical Indications & Patient Selection Criteria",
      paragraphs: [
        "Ideal candidates for referral meet the following clinical profile:",
        "• Diagnosis: Major Depressive Disorder (single episode or recurrent), typically moderate to severe (baseline PHQ-9 ≥ 10, MADRS ≥ 20).",
        "• Treatment Resistance: History of failing two or more adequate antidepressant medication trials (adequate dosage for ≥6 weeks) from at least two different classes, or documented intolerance to multiple classes.",
        "• Age: Adults aged 22 and older (Magnus Medical FDA 510(k) K220177 clearance parameters). For patients aged 18–21, standard daily TMS or SPRAVATO® can be evaluated.",
        "• Commitment: Ability to participate in a full 5-day outpatient course (Monday through Friday, approximately 9:00 AM to 5:30 PM).",
        "• For out-of-area patients: Feasibility to travel to our South Florida clinic in Coral Springs (conveniently accessible via Fort Lauderdale-Hollywood FLL, Miami MIA, or West Palm Beach PBI airports).",
      ],
    },
    {
      heading: "Safety Screening & Contraindications",
      paragraphs: [
        "Because TMS utilizes high-intensity pulsed magnetic fields and SAINT® requires pre-treatment 3T MRI brain mapping, several absolute and relative contraindications must be screened prior to scheduling:",
      ],
      list: [
        "Absolute: Non-removable ferromagnetic implants or metallic objects in or within 30 cm of the head (aneurysm clips, cochlear implants, deep brain stimulators, ocular metallic foreign bodies).",
        "Absolute: Implanted medical devices controlled by physiological signals (cardiac pacemakers, implantable cardioverter-defibrillators, vagus nerve stimulators).",
        "Absolute: Known active seizure disorders, history of unprovoked epilepsy, or conditions with increased intracranial pressure.",
        "Relative: Severe claustrophobia or inability to tolerate 3T MRI scanning (pre-scan mild anxiolytic premedication may be arranged if clinically appropriate).",
        "Relative: Active severe substance use disorders or acute psychosis, which require medical stabilization prior to elective accelerated outpatient neuromodulation.",
      ],
    },
    {
      heading: "The 4-Step Referral and Co-Management Workflow",
      paragraphs: [
        "Our structured intake ensures rapid review and constant coordination with your office:",
      ],
      table: {
        headers: ["Phase", "Clinical Activity", "Role of Referring Provider"],
        rows: [
          [
            "1. Referral & Intake",
            "Fax records and checklist to 954-827-9793. Our coordinators contact the patient within 24 hours for preliminary screening.",
            "Submits clinical history, medication log, and eligibility attestation.",
          ],
          [
            "2. Consultation & MRI",
            "Medical evaluation by Daniel Pietras, MD. 3T resting-state fMRI performed at partner imaging center; coordinates mapped via Magnus algorithm.",
            "Maintains ongoing psychiatric supportive contact and current medication regimen.",
          ],
          [
            "3. 5-Day Treatment Week",
            "50 total iTBS sessions (10/day, 1,800 pulses/session, 90% resting motor threshold) delivered in private clinical suite under direct medical supervision.",
            "Informed of patient progress; available for joint consultation if acute questions arise.",
          ],
          [
            "4. Handback & Follow-Up",
            "Comprehensive discharge report sent to your office within 5 business days detailing response, psychometrics, and maintenance guidance.",
            "Resumes primary care, ongoing pharmacotherapy, and long-term psychiatric maintenance.",
          ],
        ],
      },
    },
    {
      heading: "How to Submit a Secure Referral",
      paragraphs: [
        "To ensure compliance with HIPAA and protect sensitive psychiatric history, please do not use public unencrypted web forms. Follow these secure steps:",
        "1. Download and complete our [[Patient Eligibility Checklist|/patient-eligibility-checklist]].",
        "2. Attach recent psychiatric intake/progress notes, medication trials, and recent psychiatric rating scales (PHQ-9, MADRS, or HAM-D if available).",
        "3. Fax the referral packet directly to our secure clinical fax: 954-827-9793.",
        "4. For peer-to-peer discussions regarding complex candidacy, call 954-827-9793 to speak directly with our clinical team.",
      ],
    },
  ],
  faqs,
  sources: [
    {
      citation:
        "Cole EJ, Stimpson KH, Bentzley BS, et al. Stanford Accelerated Intelligent Neuromodulation Therapy for Treatment-Resistant Depression: A Double-Blind Randomized Controlled Trial. Am J Psychiatry. 2022;179(2):132-141.",
      url: "https://doi.org/10.1176/appi.ajp.2021.21060580",
    },
    {
      citation:
        "Rush AJ, Trivedi MH, Wisniewski SR, et al. Acute and longer-term outcomes in depressed outpatients requiring one or several treatment steps: a STAR*D report. Am J Psychiatry. 2006;163(11):1905-1917.",
      url: "https://doi.org/10.1176/ajp.2006.163.11.1905",
    },
    {
      citation:
        "U.S. Food and Drug Administration. 510(k) Premarket Notification K220177 — Magnus Medical SAINT Neuromodulation System.",
      url: "https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm?ID=K220177",
    },
    {
      citation:
        "Williams NR, Sudheimer KD, Bentzley BS, et al. High-dose spaced theta-burst TMS as a rapid-acting intervention for treatment-resistant depression: The Stanford Neuromodulation Therapy (SNT) protocol. Brain. 2021;144(7):e58.",
      url: "https://doi.org/10.1093/brain/awab145",
    },
  ],
  relatedLinks,
  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    reviewedByHref: "/dr-daniel-pietras-md",
    published: "November 3, 2026",
    lastReviewed: "November 3, 2026",
    lastUpdated: "November 3, 2026",
  },
  safetyBlock:
    "SAINT® TMS is contraindicated in patients with non-removable ferromagnetic metal in or near the head, active seizure disorders, or implanted devices sensitive to magnetic fields. Common side effects include temporary mild scalp discomfort and headache during stimulation. Seizure risk during TMS is rare (<0.1%). Neuro Wellness is an outpatient center and does not provide acute inpatient emergency stabilization. For emergency crisis support, contact 988.",
  trademarkFooter:
    "SAINT® is a registered trademark of Stanford University, exclusively licensed to Magnus Medical, Inc. NWCOA is an independent authorized provider and not affiliated with Stanford University.",
  ctaBand: {
    headline: "Partner with Us on Complex Depression Cases",
    primaryText: "Download Referral Checklist",
    primaryHref: "/patient-eligibility-checklist",
    secondaryText: "Call Clinician Line",
    secondaryHref: "tel:9548279793",
    phone: "954-827-9793",
  },
});
